<template>
  <div class="notification-inbox">
    <app-heading
      :level="2"
      :title="$t('notifications.title')"
      class="notification-inbox-heading"
    >
      <template v-if="hasUnread" #action>
        <app-link
          :label="$t('notifications.markAllAsRead')"
          class="notification-inbox-action"
          @click="onReadAll"
        />
      </template>
    </app-heading>

    <notification-item
      v-for="notification in notifications"
      :key="notification.id"
      :class="notificationClass(notification)"
      :notification="notification"
      class="notification-item"
      tabindex="0"
      @click.native="onClickNotification(notification)"
      @delete="onDeleteNotification"
      @mouseover.once.native="onHoverNotification(notification)"
    />
    <app-empty-view
      v-if="notifications.length === 0"
      :fa-icon="emptyResource.icon"
      :title="emptyResource.title"
      class="notification-inbox-empty"
      theme="white"
    />
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import type { PropType } from 'vue';
import type { IEmptyCard, INotification } from '@rready/sdk';
import { ContentType } from '@rready/sdk';

export default defineComponent({
  name: 'NotificationInbox',
  props: {
    notifications: {
      type: Array as PropType<INotification[]>,
      required: true
    },
    emptyResource: {
      type: Object as PropType<IEmptyCard>,
      required: true
    }
  },
  computed: {
    hasUnread(): boolean {
      return this.notifications.some((n) => !n.readAt);
    }
  },
  methods: {
    onReadAll() {
      this.$emit('markAllAsRead');
    },
    notificationClass(notification: INotification) {
      let className = `notification-${notification.id}`;

      if (notification.contentType === ContentType.MODAL) {
        className = `${className} modal-link`;
      }
      return className;
    },
    focusNextNotification(current: INotification) {
      const index = this.notifications.findIndex((it) => it.id === current.id);

      const focus =
        this.notifications[index + 1] ?? this.notifications[index - 1];

      if (focus) {
        const clazz = this.notificationClass(focus);
        const elements = document.getElementsByClassName(clazz);
        if (elements.length) {
          (elements[0] as HTMLElement).focus();
        }
      }
    },
    onDeleteNotification(notification: INotification) {
      this.focusNextNotification(notification);
      this.$emit('deleteNotification', notification);
    },
    onClickNotification(notification: INotification) {
      this.$emit('clickNotification', notification);
    },
    onHoverNotification(notification: INotification) {
      if (!notification.readAt) {
        this.$emit('markNotificationRead', notification);
      }
    }
  }
});
</script>

<style lang="scss" scoped>
.notification-inbox {
  display: flex;
  flex-direction: column;
  width: min(400px, 100vw);
  max-height: min(500px, 65vh);
  padding-bottom: 8px;
  overflow-y: auto;
  border-radius: var(--radius-3);
  background-color: var(--color-white);
  box-shadow: 0 0 46px rgba(0, 0, 0, 0.07), 0 0 10px rgba(179, 179, 179, 0.2);
}

.notification-inbox-heading {
  position: sticky;
  z-index: 3;
  top: 0;
  padding: 16px 16px 4px 16px;
  background-color: var(--color-white);
  font-size: var(--font-size-normal);
  line-height: calc(var(--font-size-normal) * 1.5);
}

.notification-inbox-action {
  cursor: pointer;
  font-size: var(--font-size-small);
  color: var(--color-grey-light-2) !important;
  text-decoration: none !important;
}

.notification-item {
  padding-left: 16px;
  padding-right: 16px;

  .modal-link {
    cursor: pointer;
  }
}
</style>
